import { useState } from "react";
import { useFeeOptions, useStandardDefault } from "@/components/AdvertisingTermsPicker";
import { toast } from "sonner";
import { UserPlus, Loader2 } from "lucide-react";
import { createInvitation } from "@/lib/inviteApi";
import { errMsg } from "@/lib/applyApi";
import { copyText } from "@/lib/clipboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const FEES = (o) => [o.standard, o.reduced, "custom"];

const ENVS = [["live", "LIVE"], ["test", "TEST"]];

// mode "live"/"test" (Pipeline filter) fixes the environment; anything else (ALL, Applications page) requires an explicit choice.
export function InviteDialog({ open, onClose, onCreated, mode }) {
  const fixed = mode === "live" || mode === "test" ? mode : null;
  const [picked, setPicked] = useState(null);
  const env = fixed || picked;
  const [fee, setFee] = useState("10");
  const opts = useFeeOptions();
  const touch = useStandardDefault(fee, setFee, opts);
  const [custom, setCustom] = useState("");
  const [v, setV] = useState({ seller_name: "", seller_email: "", horse_name: "" });
  const [busy, setBusy] = useState(false);
  const [made, setMade] = useState(null);
  const reset = () => { setPicked(null); setFee(opts.standard); setCustom(""); setV({ seller_name: "", seller_email: "", horse_name: "" }); setMade(null); };
  const close = () => { reset(); onClose(); };
  const create = async () => {
    setBusy(true);
    try {
      const inv = await createInvitation({ advertising_fee_percent: Number(fee === "custom" ? custom : fee), ...v, is_test: env === "test" });
      setMade(inv); onCreated(inv); toast.success("Invitation created");
    } catch (e) { toast.error(errMsg(e, "Could not create invitation")); }
    finally { setBusy(false); }
  };
  const field = (k, label, type = "text") => (
    <div className="space-y-1">
      <Label htmlFor={`inv-${k}`} className="text-sm">{label} <span className="text-stone-400 font-normal">(optional)</span></Label>
      <Input id={`inv-${k}`} data-testid={`invite-${k.replace("_", "-")}`} type={type} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} className="h-11" />
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="invite-dialog" className="w-[calc(100%-1.5rem)] max-w-lg max-h-[90vh] overflow-y-auto rounded-xl">
        <DialogHeader>
          <DialogTitle>Invite Pre-Approved Seller</DialogTitle>
          <DialogDescription>Creates a private 30-day link. The seller verifies their email, signs the standard agreement with your fee, then completes their Horse Ad Template — no application approval needed.</DialogDescription>
        </DialogHeader>
        {made ? (
          <div data-testid="invite-created" className="space-y-3">
            <p className="text-sm text-stone-700">Send this link by Messenger, text or email:</p>
            <div data-testid="invite-created-url" className="rounded-lg border border-stone-200 bg-stone-50 p-3 text-xs break-all font-mono">{made.url}</div>
            <Button data-testid="invite-copy-new" onClick={() => copyText(made.url)} className="w-full h-11 rounded-full bg-amber-800 hover:bg-amber-900 text-white">Copy Link</Button>
          </div>
        ) : (
          <div className="space-y-4">
            {fixed ? (
              <p data-testid="invite-env-fixed" className={`text-sm font-semibold ${fixed === "test" ? "text-amber-800" : "text-emerald-800"}`}>{fixed === "test" ? "TEST invitation — the seller enters the TEST workflow." : "LIVE invitation — the seller enters the LIVE workflow."}</p>
            ) : (
              <div className="space-y-1.5">
                <Label className="text-sm">Environment <span className="text-amber-800">*</span></Label>
                <div role="radiogroup" className="flex gap-2">
                  {ENVS.map(([k, l]) => (
                    <button key={k} type="button" role="radio" aria-checked={picked === k} data-testid={`invite-env-${k}`} onClick={() => setPicked(k)}
                      className={`h-11 px-4 rounded-full border text-sm font-semibold transition-colors duration-150 ${picked === k ? "bg-amber-800 border-amber-800 text-white" : "bg-white border-stone-300 text-stone-700 hover:border-amber-700"}`}>{l}</button>
                  ))}
                </div>
                {!picked && <p data-testid="invite-env-required" className="text-xs text-stone-500">Choose LIVE or TEST for this invitation.</p>}
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-sm">Advertising Fee</Label>
              <div role="radiogroup" className="flex flex-wrap gap-2">
                {FEES(opts).map((f, i) => (
                  <button key={i} type="button" role="radio" aria-checked={fee === f} data-testid={`invite-fee-${["standard", "reduced", "custom"][i]}`} onClick={() => { touch(); setFee(f); }}
                    className={`h-11 px-4 rounded-full border text-sm font-semibold transition-colors duration-150 ${fee === f ? "bg-amber-800 border-amber-800 text-white" : "bg-white border-stone-300 text-stone-700 hover:border-amber-700"}`}>
                    {f === "custom" ? "Custom" : `${f}%${i === 0 ? " (default)" : ""}`}
                  </button>
                ))}
              </div>
              {fee === "custom" && <Input data-testid="invite-fee-custom-input" inputMode="decimal" placeholder="e.g. 7.5" value={custom} onChange={(e) => setCustom(e.target.value)} className="h-11 w-32" />}
            </div>
            {field("seller_name", "Seller name")}
            {field("seller_email", "Seller email", "email")}
            {field("horse_name", "Horse name")}
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" data-testid="invite-close" onClick={close} className="h-11 rounded-full">{made ? "Done" : "Cancel"}</Button>
          {!made && <Button data-testid="invite-create" onClick={create} disabled={busy || !env || (fee === "custom" && !custom)} className="h-11 rounded-full bg-amber-800 hover:bg-amber-900 text-white">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="h-4 w-4 mr-1.5" />Generate Link</>}
          </Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
